import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../../Firebase";
import "../../Admin.css";
import AdminNav from "../../components/AdminNav";

function formatDate(v) {
  if (!v) return "";
  if (typeof v === "object" && v?.toDate) {
    const d = v.toDate();
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function shortId(id) {
  if (!id) return "";
  const s = String(id);
  return s.length <= 10 ? s : `${s.slice(0, 4)}…${s.slice(-4)}`;
}

export default function AdminReportsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const [statusFilter, setStatusFilter] = useState("all"); // all|draft|published|archived
  const [searchText, setSearchText] = useState("");

  const [lastDoc, setLastDoc] = useState(null);
  const PAGE_SIZE = 20;

  useEffect(() => {
    document.title = "Reports | Admin";
  }, []);

  async function loadFirstPage() {
    setLoading(true);
    setErr("");
    setRows([]);
    setLastDoc(null);

    try {
      const base = collection(db, "publicReports");

      // Query rules:
      // - Firestore can’t do OR text search without an index strategy.
      // - We’ll load a page and do client-side filtering for searchText.
      // - Status filter is done server-side when possible.
      let q = query(base, orderBy("updatedAt", "desc"), limit(PAGE_SIZE));

      if (statusFilter !== "all") {
        q = query(base, where("status", "==", statusFilter), orderBy("updatedAt", "desc"), limit(PAGE_SIZE));
      }

      // If you don’t have updatedAt yet, switch to createdAt:
      // orderBy("createdAt","desc")
      // (You must keep this consistent.)

      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setRows(docs);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
    } catch (e) {
      setErr(e?.message || "Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!lastDoc) return;
    setLoadingMore(true);
    setErr("");

    try {
      const base = collection(db, "publicReports");
      let q = query(base, orderBy("updatedAt", "desc"), startAfter(lastDoc), limit(PAGE_SIZE));

      if (statusFilter !== "all") {
        q = query(
          base,
          where("status", "==", statusFilter),
          orderBy("updatedAt", "desc"),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      }

      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setRows((prev) => [...prev, ...docs]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
    } catch (e) {
      setErr(e?.message || "Failed to load more reports.");
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    // reload whenever status changes
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filteredRows = useMemo(() => {
    const t = searchText.trim().toLowerCase();
    if (!t) return rows;

    return rows.filter((r) => {
      const hay = [
        r.id,
        r.propertyName,
        r.addressLine1,
        r.cityStateZip,
        r.preparedFor,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(t);
    });
  }, [rows, searchText]);

  async function copyPublicLink(id) {
    const url = `${window.location.origin}/report/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      // light feedback without any dependency
      alert("Copied link to clipboard");
    } catch {
      prompt("Copy this link:", url);
    }
  }

  return (
    <div className="arlPage">
      <AdminNav />
      <div className="arlTop">
        <div>
          <h1>Inspection Reports</h1>
          <p className="arlSub">All reports stored in <code>publicReports</code>.</p>
        </div>

        <div className="arlTopActions">
          <Link className="btn btnPrimary" to="/admin/reports/new">
            + New Report
          </Link>
        </div>
      </div>

      <div className="arlControls">
        <label className="field">
          <span>Status</span>
          <select
            className="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </label>

        <label className="field arlSearch">
          <span>Search</span>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search property, address, prepared for, or blob id…"
          />
        </label>

        <button className="btn btnGhost" onClick={loadFirstPage} disabled={loading}>
          Refresh
        </button>
      </div>

      {err ? <div className="arlError">{err}</div> : null}

      <div className="arlTableWrap">
        <table className="arlTable">
          <thead>
            <tr>
              <th>Status</th>
              <th>Property</th>
              <th>Location</th>
              <th>Report Date</th>
              <th>Updated</th>
              <th>Blob ID</th>
              <th style={{ width: 320 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="arlEmpty">Loading…</td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="arlEmpty">No reports found.</td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span className={`badge badge-${r.status || "draft"}`}>
                      {r.status || "draft"}
                    </span>
                  </td>
                  <td className="cellMain">
                    <div className="cellTitle">{r.propertyName || "—"}</div>
                    <div className="cellSub">{r.preparedFor || ""}</div>
                  </td>
                  <td>{r.cityStateZip || r.addressLine1 || "—"}</td>
                  <td>{formatDate(r.reportDate) || "—"}</td>
                  <td>{formatDate(r.updatedAt || r.createdAt) || "—"}</td>
                  <td><code>{shortId(r.id)}</code></td>
                  <td className="actions">
                    <Link className="btn btnTiny" to={`/report/${r.id}`} target="_blank" rel="noreferrer">
                      View Public
                    </Link>

                    {/* Placeholder for edit page once you add it */}
                    <Link className="btn btnTiny btnGhost" to={`/admin/reports/edit/${r.id}`}>
                    Edit
                    </Link>

                    <button className="btn btnTiny btnGhost" onClick={() => copyPublicLink(r.id)}>
                      Copy Link
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="arlBottom">
        <div className="arlCount">
          Showing <strong>{filteredRows.length}</strong> result(s)
          {searchText.trim() ? <span className="muted"> (filtered)</span> : null}
        </div>

        <button className="btn" onClick={loadMore} disabled={loadingMore || !lastDoc || loading}>
          {loadingMore ? "Loading…" : lastDoc ? "Load More" : "No More"}
        </button>
      </div>

      <div className="arlNote">
        <div className="muted">
          Note: search is client-side (Firestore doesn’t support full text search without extra indexing).
        </div>
      </div>
    </div>
  );
}
